import React, { useState } from "react";
import { SalesByAgeGroupProps } from "./types";
import { Grid, Switch } from "@material-ui/core";
import useStyles from "./styles";
import BarChart from "../../components/BarChart";
import UserDetails from "../UserDetails";
import { BarChartData } from "../../components/BarChart/types";
import StatTable from "../../components/StatTable";
import { Currency } from "../../../../components/misc/Currency";
import { useI18n } from "../../../../hooks/useI18n";

const SalesByAgeGroup = ({
  storeName,
  isLoading,
  periodFrom,
  periodTo,
  userStatistics
}: SalesByAgeGroupProps) => {
  const classes = useStyles();
  const [showGraph, setShowGraph] = useState(false);
  const { t } = useI18n();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowGraph(event.target.checked);
  };

  const populateAgeTable = () => {
    const ageGroups = userStatistics.reduce((acc: any, curr) => {
      if (curr.storeName === storeName) {
        let index = acc.findIndex((a: { age: number }) => a.age === curr.age);
        if (index < 0 && curr.age >= 0) {
          acc.push({
            storeId: curr.storeId,
            age: curr.age,
            salesMen: curr.gender === 1 ? curr.sales : 0,
            salesCountMen: curr.gender === 1 ? curr.salesCount : 0,
            salesWomen: curr.gender === 0 ? curr.sales : 0,
            salesCountWomen: curr.gender === 0 ? curr.salesCount : 0,
            sales: curr.sales,
            salesCount: curr.salesCount
          });
        } else {
          acc[index].salesMen += curr.gender === 1 ? curr.sales : 0;
          acc[index].salesCountMen += curr.gender === 1 ? curr.salesCount : 0;
          acc[index].salesWomen += curr.gender === 0 ? curr.sales : 0;
          acc[index].salesCountWomen += curr.gender === 0 ? curr.salesCount : 0;
          acc[index].sales += curr.sales;
          acc[index].salesCount += curr.salesCount;
        }
      }

      return acc;
    }, []);

    return ageGroups.map(
      (a: {
        age: number;
        sales: number;
        salesMen: number;
        salesWomen: number;
        salesCount: number;
        salesCountMen: number;
        salesCountWomen: number;
        storeName: string;
        storeId: string;
      }) => ({
        storeId: a.storeId,
        ageFormatted: `${a.age} < ${a.age + 9}`,
        age: a.age,
        sales: a.sales,
        salesMen: a.salesMen,
        salesWomen: a.salesWomen,
        purchases: a.salesCount,
        purchasesMen: a.salesCountMen,
        purchasesWomen: a.salesCountWomen
      })
    );
  };

  const populateAgeChart = (): BarChartData => {
    const ageGroups = userStatistics.reduce((acc: any, curr) => {
      if (curr.storeName === storeName) {
        if (!acc[curr.age] && curr.age >= 0) {
          acc[curr.age] = { sales: 0, salesMen: 0, salesWomen: 0 };
        }
        acc[curr.age].sales += curr.sales;
        acc[curr.age].salesMen += curr.gender === 1 ? curr.sales : 0;
        acc[curr.age].salesWomen += curr.gender === 0 ? curr.sales : 0;
      }
      return acc;
    }, {});

    return {
      labels: Object.keys(ageGroups).map(a => `${a} < ${Number(a) + 9}`),
      datasets: [
        {
          label: "Sales",
          rgbColor: { r: 0, g: 148, b: 148 },
          data: Object.keys(ageGroups).map(k => ageGroups[k].sales)
        },
        {
          label: t("common.tableHeaders.salesWomen"),
          rgbColor: { r: 58, g: 160, b: 75 },
          data: Object.keys(ageGroups).map(k => ageGroups[k].salesWomen)
        },
        {
          label: t("common.tableHeaders.salesMen"),
          rgbColor: { r: 5, g: 89, b: 141 },
          data: Object.keys(ageGroups).map(k => ageGroups[k].salesMen)
        }
      ]
    } as BarChartData;
  };

  return (
    <div>
      <Grid
        className={
          showGraph ? classes.switchContainer : classes.switchContainerAbsolute
        }
        component="label"
        container
        justifyContent="flex-start"
        alignItems="center"
        spacing={1}
      >
        <Grid item>{t("common.messages.table")}</Grid>
        <Grid item>
          <Switch
            size={"small"}
            checked={showGraph}
            onChange={handleChange}
            name="graphSwitch"
          />
        </Grid>
        <Grid item>{t("common.messages.chart")}</Grid>
      </Grid>
      {showGraph ? (
        <BarChart
          chartData={populateAgeChart()}
          showLegend={true}
          isLoading={isLoading}
        />
      ) : (
        <StatTable
          isLoading={isLoading}
          exportName={"Age group statistics"}
          columns={[
            {
              title: "StoreId",
              field: "storeId",
              hidden: true
            },
            {
              title: "AgeNo",
              field: "age",
              hidden: true
            },
            {
              title: "Age",
              field: "ageFormatted",
              cellStyle: {
                padding: "8px"
              }
            },
            {
              title: "Sales",
              field: "sales",
              cellStyle: { borderLeft: "1px solid #e9e5e2", padding: "8px" },
              render: (rowData: any) => <Currency value={rowData.sales} />
            },
            {
              title: t("common.tableHeaders.byWomen"),
              field: "salesWomen",
              cellStyle: {
                padding: "8px"
              },
              render: (rowData: any) => <Currency value={rowData.salesWomen} />
            },
            {
              title: t("common.tableHeaders.byMen"),
              field: "salesMen",
              cellStyle: {
                padding: "8px"
              },
              render: (rowData: any) => <Currency value={rowData.salesMen} />
            },
            {
              title: "Purchases",
              field: "purchases",
              cellStyle: { borderLeft: "1px solid #e9e5e2", padding: "8px" }
            },
            {
              title: t("common.tableHeaders.byWomen"),
              field: "purchasesWomen",
              cellStyle: {
                padding: "8px"
              }
            },
            {
              title: t("common.tableHeaders.byMen"),
              field: "purchasesMen",
              cellStyle: {
                padding: "8px"
              }
            }
          ]}
          data={!isLoading ? populateAgeTable() : []}
          detailPanel={(rowData: any) => (
            <UserDetails
              storeId={rowData.storeId}
              periodFrom={periodFrom}
              periodTo={periodTo}
              ageFrom={rowData.age}
              ageTo={rowData.age + 10}
            />
          )}
          dense
        />
      )}
    </div>
  );
};
export default SalesByAgeGroup;
